import React from 'react';
import { ArrowUpRight, ArrowDownRight, Receipt } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { KPICard, CustomTooltip, StatementHeader, SectionRow } from './ReportHelpers';

export function VATReport({ reportData, viewMode, entityName }) {
  if (!reportData || !reportData.input_vat) return null;
  const { input_vat, output_vat, net_payable, rate_breakdown, tax_name, country, is_refund } = reportData;

  if (viewMode === 'list') {
    return (
      <Card data-testid="vat-list-view">
        <CardContent className="py-8 px-6 max-w-3xl mx-auto">
          <StatementHeader entityName={entityName} title={`${tax_name} Return — ${country}`} period={reportData.period} />
          <table className="w-full text-sm">
            <tbody>
              <SectionRow label={`OUTPUT ${tax_name.toUpperCase()} — Collected on Sales`} bold />
              <SectionRow label={output_vat.account || 'Output VAT'} indent />
              {output_vat.entries.map((e, idx) => (
                <tr key={`out-${e.date}-${idx}`} className="border-b border-zinc-200 hover:bg-zinc-50/50">
                  <td className="py-1.5 pl-10 text-sm text-zinc-500">{formatDate(e.date)} — {e.description}</td>
                  <td className="py-1.5 pr-6 text-right font-mono text-sm tabular-nums text-zinc-900">{formatCurrency(e.amount)}</td>
                </tr>
              ))}
              <SectionRow label={`Total Output ${tax_name}`} amount={output_vat.total} total />
              <tr><td className="py-3" colSpan="2"></td></tr>
              <SectionRow label={`INPUT ${tax_name.toUpperCase()} — Paid on Purchases`} bold />
              <SectionRow label={input_vat.account || 'Input VAT'} indent />
              {input_vat.entries.map((e, idx) => (
                <tr key={`in-${e.date}-${idx}`} className="border-b border-zinc-200 hover:bg-zinc-50/50">
                  <td className="py-1.5 pl-10 text-sm text-zinc-500">{formatDate(e.date)} — {e.description}</td>
                  <td className="py-1.5 pr-6 text-right font-mono text-sm tabular-nums text-zinc-900">{formatCurrency(e.amount)}</td>
                </tr>
              ))}
              <SectionRow label={`Total Input ${tax_name}`} amount={input_vat.total} total />
              <tr><td className="py-3" colSpan="2"></td></tr>
              {rate_breakdown.length > 0 && (
                <>
                  <SectionRow label="RATE BREAKDOWN" bold />
                  {rate_breakdown.map(r => (
                    <SectionRow key={`rate-${r.rate}`} label={`${r.rate}% — ${r.count} transaction(s), Taxable: ${formatCurrency(r.taxable_base)}`} amount={r.tax_amount} indent />
                  ))}
                  <tr><td className="py-3" colSpan="2"></td></tr>
                </>
              )}
              <SectionRow label={is_refund ? 'VAT REFUND DUE' : `NET ${tax_name.toUpperCase()} PAYABLE`} amount={is_refund ? -Math.abs(net_payable) : net_payable} grandTotal />
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  }

  const comparisonData = [
    { name: `Output ${tax_name}`, amount: output_vat.total },
    { name: `Input ${tax_name}`, amount: input_vat.total },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title={`Output ${tax_name}`} subtitle="Collected on sales" value={formatCurrency(output_vat.total)} icon={ArrowUpRight} color="orange" />
        <KPICard title={`Input ${tax_name}`} subtitle="Paid on purchases" value={formatCurrency(input_vat.total)} icon={ArrowDownRight} color="blue" />
        <KPICard title={is_refund ? 'Refund Due' : 'Net Payable'} subtitle={`${country} - ${tax_name}`} value={formatCurrency(Math.abs(net_payable))} icon={Receipt} color={is_refund ? 'green' : 'red'} />
      </div>

      {(output_vat.total > 0 || input_vat.total > 0) && (
        <Card>
          <CardHeader><CardTitle className="text-base">{tax_name} Comparison</CardTitle></CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" name="Amount" radius={[0, 6, 6, 0]}>
                    <Cell key="output" fill="#D94E18" />
                    <Cell key="input" fill="#60A5FA" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {rate_breakdown.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Rate Breakdown</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="report-table" data-testid="vat-rate-table">
              <thead><tr><th className="pl-4">Rate</th><th className="text-right">Transactions</th><th className="text-right">Taxable Base</th><th className="text-right pr-4">Tax Amount</th></tr></thead>
              <tbody>
                {rate_breakdown.map(r => (
                  <tr key={`rate-${r.rate}`}>
                    <td className="font-mono pl-4"><Badge variant="outline">{r.rate}%</Badge></td>
                    <td className="text-right text-zinc-500">{r.count}</td>
                    <td className="text-right font-mono">{formatCurrency(r.taxable_base)}</td>
                    <td className="text-right font-mono pr-4">{formatCurrency(r.tax_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm text-zinc-500 uppercase tracking-wider">Output {tax_name} Entries</CardTitle></CardHeader>
          <CardContent>
            {output_vat.entries.length === 0 ? <p className="text-sm text-zinc-400">No entries</p> : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {output_vat.entries.map((e, idx) => (
                  <div key={`out-${e.date}-${idx}`} className="flex justify-between text-sm py-1 border-b border-zinc-100">
                    <div><span className="text-zinc-400">{formatDate(e.date)}</span> <span className="ml-2">{e.description}</span></div>
                    <span className="font-mono text-[#D94E18]">{formatCurrency(e.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm text-zinc-500 uppercase tracking-wider">Input {tax_name} Entries</CardTitle></CardHeader>
          <CardContent>
            {input_vat.entries.length === 0 ? <p className="text-sm text-zinc-400">No entries</p> : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {input_vat.entries.map((e, idx) => (
                  <div key={`in-${e.date}-${idx}`} className="flex justify-between text-sm py-1 border-b border-zinc-100">
                    <div><span className="text-zinc-400">{formatDate(e.date)}</span> <span className="ml-2">{e.description}</span></div>
                    <span className="font-mono text-blue-600">{formatCurrency(e.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className={`p-5 rounded-xl ${is_refund ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex justify-between items-center">
          <div>
            <span className="font-semibold text-zinc-900">{is_refund ? 'VAT Refund Due' : 'VAT Net Payable'}</span>
            <p className="text-xs text-zinc-500 mt-0.5">Output {formatCurrency(output_vat.total)} - Input {formatCurrency(input_vat.total)}</p>
          </div>
          <span className={`text-3xl font-mono font-bold ${is_refund ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(Math.abs(net_payable))}</span>
        </div>
      </div>
    </div>
  );
}
